import React, { createContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import useApi from '../hooks/useApi';
import api from '../api';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [session, setSession] = useState({
    role: 'GUEST',
    username: '',
    roles: ['GUEST'],
    userId: null,
    communityId: null,
    communityName: '',
    communityDisplayName: '',
    community: null
  });
  const [loading, setLoading] = useState(true);
  const { get } = useApi();

  const isSuperAdmin = session.roles.includes('SUPER_ADMIN');
  const isAdmin = session.roles.includes('ADMIN');
  const canEditRoles = isSuperAdmin || isAdmin;

  console.log('AuthContext - session:', session, 'isSuperAdmin:', isSuperAdmin, 'isAdmin:', isAdmin, 'canEditRoles:', canEditRoles);

  const validateSession = useCallback(async () => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const username = localStorage.getItem('username');
    const userId = localStorage.getItem('userId');
    const communityId = localStorage.getItem('communityId');
    const communityName = localStorage.getItem('communityName');
    const communityDisplayName = localStorage.getItem('communityDisplayName');

    if (!token || !role || !username) {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('username');
      localStorage.removeItem('userId');
      localStorage.removeItem('communityId');
      localStorage.removeItem('communityName');
      localStorage.removeItem('communityDisplayName');
      setSession({
        role: 'GUEST',
        username: '',
        roles: ['GUEST'],
        userId: null,
        communityId: null,
        communityName: '',
        communityDisplayName: '',
        community: null
      });
      setLoading(false);
      return false;
    }

    try {
      const user = await get('/api/users/current');
      const community = user.community || {};
      setSession({
        role,
        username,
        roles: role === 'GUEST' ? ['GUEST'] : role.split(','),
        userId: user.id || userId,
        name: user.name || '',
        communityId: community.id || communityId,
        communityName: community.name || communityName || '',
        communityDisplayName: community.displayName || communityDisplayName || '',
        community
      });
      localStorage.setItem('userId', user.id || userId);
      localStorage.setItem('communityId', community.id || communityId || '');
      localStorage.setItem('communityName', community.name || communityName || '');
      localStorage.setItem('communityDisplayName', community.displayName || communityDisplayName || '');
      return true;
    } catch (err) {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('username');
      localStorage.removeItem('userId');
      localStorage.removeItem('communityId');
      localStorage.removeItem('communityName');
      localStorage.removeItem('communityDisplayName');
      toast.error('Session expired.');
      setSession({
        role: 'GUEST',
        username: '',
        roles: ['GUEST'],
        userId: null,
        communityId: null,
        communityName: '',
        communityDisplayName: '',
        community: null
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  const login = async (token, role, username) => {
    localStorage.setItem('token', token);
    localStorage.setItem('role', role);
    localStorage.setItem('username', username);
    setLoading(true);
    return await validateSession();
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout', {});
    } catch (err) {
      console.error('Logout failed:', err);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    localStorage.removeItem('userId');
    localStorage.removeItem('communityId');
    localStorage.removeItem('communityName');
    localStorage.removeItem('communityDisplayName');
    toast.success('Logged out.');
    setSession({
      role: 'GUEST',
      username: '',
      roles: ['GUEST'],
      userId: null,
      communityId: null,
      communityName: '',
      communityDisplayName: '',
      community: null
    });
  };

  return (
    <AuthContext.Provider value={{ session, loading, login, logout, validateSession, isSuperAdmin, isAdmin, canEditRoles }}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
